import * as THREE from 'three'

import css from '@css/sass/styles.module.scss'
import { AssetManager } from '@js/loaders'
import { Marquee } from '@js/utils'
import { Thib, Room } from '../models'
import { BaseScene } from '../core'

/* ___________________________________________________________________________
|                                 L O C A L                                   |
|___________________________________________________________________________ */

class SubtitleBar {
  private el: HTMLParagraphElement

  constructor() {
    const container = document.createElement('div')
    container.id = css.subtitle
    document.body.appendChild(container)

    this.el = document.createElement('p')
    this.el.classList.add(css.unselectable_text)
    container.appendChild(this.el)
  }

  update(text: string): void {
    new Marquee(this.el, { text, speed: 150 }).start()
  }
}

class CloseButton {
  private btn: HTMLInputElement

  constructor() {
    const assets = AssetManager.getInstance()
    const exit1 = assets.get('icons', 'croix.svg')
    const exit2 = assets.get('icons', 'croix_2.svg')

    this.btn = document.createElement('input') as HTMLInputElement
    this.btn.type = 'image'
    this.btn.classList.add(css.btnClose)
    this.btn.style.display = 'block'
    if (exit2) this.btn.src = exit2

    this.btn.addEventListener('mouseenter', () => { if (exit1) this.btn.src = exit1 })
    this.btn.addEventListener('mouseleave', () => { if (exit2) this.btn.src = exit2 })
    this.btn.addEventListener('click',      () => { window.location.href = './index.html' })

    document.body.appendChild(this.btn)
  }
}

/* ___________________________________________________________________________
|                         C H A T R O O M   S C E N E                        |
|___________________________________________________________________________ */


class ChatroomScene extends BaseScene {

  private thib!: Thib 
  private room!: Room
  private readonly CENTER = new THREE.Vector3(0, -1.5, -3)

  constructor() {
    // Minimal constructor — renderer is rebuilt once the Blender camera is known
    super(css.cnv)
    this.canvas.style.backgroundColor = 'black'
    this.scene.fog = new THREE.Fog(0x000000, 0, 100)
  }


  async loadModels(): Promise<void> {
    /**
     * Load order:
     *   1. Room GLTF — contains the Blender camera and THIB_LOCATION marker
     *   2. Camera extracted → renderer rebuilt with real camera
     *   3. Thib GLTF — placed at THIB_LOCATION
     *
     * Because the camera comes from Blender, we can't pass real renderer options
     * to BaseScene's constructor. We pass empty options and rebuild after room loads.
     */

    this.room = new Room({renderOrder: 998})
    await this.room.load(this.scene)

    // Use room camera
    this.camera = this.room.camera

    // Rebuild renderer now that real camera is known
    this.renderer = this.setupRenderer(this.canvas, this.camera, this.scene, {
      composer: true,
      saturation: -0.2,
      contrast: -0.1,
      bloom: { strength: 0.2, radius: 0.2, threshold: 0.15 }
    })

    this.room.playCameraAnimation('camera_idle')

    // Get marker
    const thibLocation = this.room.getMarker('THIB_LOCATION')
    if (!thibLocation) throw new Error('THIB_LOCATION not found')

    this.thib = new Thib({
      transform: {
        position: thibLocation.position,
        rotation: { x: 0, y: 159, z: 0 },
        scale: 1.25
      },
      animations: ['lying', 'queue', 'clicking', 'head_turning'],
      animatorOptions: {
        renderer: this.renderer,
        center: this.CENTER
      },
      renderOrder: 999
    }, this.camera)

    await this.thib.load(this.scene)
  }

  protected update(delta: number): void {
    this.thib?.update(delta)
    this.room?.update(delta)
  }

  start(): void {
    this.startLoop()
    console.debug('[ChatroomScene] Render loop started')
  }

}

/* ___________________________________________________________________________
|                                M O D U L E                                  |
|___________________________________________________________________________ */


export const setupChatroomScene = (): void => {
  new CloseButton()

  const subtitleBar = new SubtitleBar()
  subtitleBar.update(
    "Nous sommes vraiment désolés, Thibault se sent un peu fatigué pour le moment... " +
    "Merci de quitter cette pièce sans faire de bruit."
  )

  const scene = new ChatroomScene()
  scene.loadModels()
  scene.start()
  console.debug('[ChatroomScene] set up.')
}
